import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PencilIcon, PlusIcon, Trash2Icon } from "lucide-react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AddOrganisationMembershipDialog } from "@/components/AddOrganisationMembershipDialog";
import { EditUserRoleDialog } from "@/components/EditUserRoleDialog";
import { RemoveOrganisationMembershipDialog } from "@/components/RemoveOrganisationMembershipDialog";
import { UserSignInActivity } from "@/components/user-activity/UserSignInActivity";
import { formatDateTime } from "@/lib/format";

type User = components["schemas"]["GetUserResponse"];
type UserOrganisation = components["schemas"]["UserOrganisationResponse"];
type OrganisationRole = components["schemas"]["OrganisationRole"];

export function UserPage() {
  const { userId } = useParams<{ userId: string }>();

  const [user, setUser] = useState<User | null>(null);
  const [userLoading, setUserLoading] = useState(true);
  const [organisations, setOrganisations] = useState<UserOrganisation[]>([]);
  const [orgsLoading, setOrgsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<UserOrganisation | null>(null);
  const [removing, setRemoving] = useState<UserOrganisation | null>(null);
  const [adding, setAdding] = useState(false);
  const [orgsRefreshKey, setOrgsRefreshKey] = useState(0);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    async function fetchUser() {
      setUserLoading(true);
      const { data, error: apiError } = await api.GET("/admin/users/{id}", {
        params: { path: { id: userId! } },
      });
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load user.");
      } else {
        setUser(data ?? null);
      }
      setUserLoading(false);
    }

    fetchUser();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    async function fetchOrganisations() {
      setOrgsLoading(true);
      const { data, error: apiError } = await api.GET(
        "/users/{id}/organisations",
        {
          params: { path: { id: userId! } },
        },
      );
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load user organisations.");
      } else {
        setOrganisations(data ?? []);
      }
      setOrgsLoading(false);
    }

    fetchOrganisations();
    return () => {
      cancelled = true;
    };
  }, [userId, orgsRefreshKey]);

  function handleRoleUpdated(organisationId: string, newRole: OrganisationRole) {
    setOrganisations((prev) =>
      prev.map((o) =>
        o.organisationId === organisationId ? { ...o, role: newRole } : o,
      ),
    );
  }

  if (error) {
    return (
      <div className="mx-auto w-full max-w-[100rem] p-8">
        <p className="text-destructive">{error}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[100rem] p-8">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">User</h1>
      </div>

      {userLoading ? (
        <div className="flex justify-center py-4">
          <Spinner className="size-5" />
        </div>
      ) : user ? (
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>
              {user.name || (
                <span className="text-muted-foreground">Unnamed</span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid [&>div]:min-w-0 grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <dt className="text-muted-foreground text-sm">User ID</dt>
                <dd className="mt-1">
                  <Badge variant="secondary" className="font-mono text-xs">
                    {user.id}
                  </Badge>
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground text-sm">Email</dt>
                <dd className="mt-1 text-sm">{user.email}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground text-sm">Created</dt>
                <dd className="mt-1 text-sm">
                  {formatDateTime(user.createdAt)}
                </dd>
              </div>
            </dl>
          </CardContent>
        </Card>
      ) : null}

      {userId && <UserSignInActivity userId={userId} />}

      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold">Organisations</h2>
        <Button size="sm" onClick={() => setAdding(true)} disabled={!userId}>
          <PlusIcon className="size-4" />
          Add organisation
        </Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Organisation ID</TableHead>
              <TableHead>Industry</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead className="w-[100px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {organisations.length === 0 && !orgsLoading ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="text-muted-foreground h-24 text-center"
                >
                  No organisations found.
                </TableCell>
              </TableRow>
            ) : (
              organisations.map((org) => (
                <TableRow key={org.organisationId}>
                  <TableCell className="font-medium">
                    <Link
                      to={`/organisations/${org.organisationId}`}
                      className="text-primary hover:underline"
                    >
                      {org.organisationName}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link to={`/organisations/${org.organisationId}`}>
                      <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
                        {org.organisationId}
                      </Badge>
                    </Link>
                  </TableCell>
                  <TableCell>
                    {org.industryDisplayName || (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">
                      {org.role}
                    </Badge>
                  </TableCell>
                  <TableCell>{formatDateTime(org.joinedAt)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-8"
                        onClick={() => setEditing(org)}
                        aria-label="Edit role"
                      >
                        <PencilIcon className="size-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive size-8"
                        onClick={() => setRemoving(org)}
                        aria-label="Remove from organisation"
                      >
                        <Trash2Icon className="size-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {orgsLoading && (
        <div className="flex justify-center py-4">
          <Spinner className="size-5" />
        </div>
      )}

      {userId && (
        <AddOrganisationMembershipDialog
          open={adding}
          onOpenChange={setAdding}
          userId={userId}
          userLabel={user?.name || user?.email || undefined}
          onAdded={() => setOrgsRefreshKey((k) => k + 1)}
        />
      )}

      {removing && removing.organisationId && userId && (
        <RemoveOrganisationMembershipDialog
          open={removing !== null}
          onOpenChange={(open) => !open && setRemoving(null)}
          userId={userId}
          userLabel={user?.name || user?.email || "This user"}
          organisationId={removing.organisationId}
          organisationName={removing.organisationName}
          onRemoved={() => setOrgsRefreshKey((k) => k + 1)}
        />
      )}

      {editing && editing.organisationId && userId && (
        <EditUserRoleDialog
          open={editing !== null}
          onOpenChange={(open) => !open && setEditing(null)}
          userId={userId}
          organisationId={editing.organisationId}
          organisationName={editing.organisationName}
          currentRole={editing.role as OrganisationRole}
          onUpdated={(newRole) => {
            if (editing.organisationId) {
              handleRoleUpdated(editing.organisationId, newRole);
            }
          }}
        />
      )}
    </div>
  );
}
